// kaynak-referans user/state/CustomStatus.ts 1-1 port.
//
// Özel durum (custom status) veri modeli ve saf yardımcı fonksiyonlar.
// - `CustomStatus`: uygulama-içi biçim (camelCase alanlar).
// - Gateway (snake_case) ve API payload'ları arasında dönüştürücüler.
// - Normalizasyon: metni kırpar/uzunluk sınırına indirir, boş durumu null yapar,
//   süresi geçmiş durumu (expiresAt) null'a düşürür.
//
// kaynak-referanstan fark (Tebhos konvansiyonu): kaynak-referanstaki
// `UnicodeEmojis.normalizeEmojiNameToSurrogate` (shortcode adını native surrogate'e
// çeviren tablo) TEBHOS'ta yok; TEBHOS'ta özel durum emojisi zaten native karakter
// olarak tutulduğu için passthrough (trim edilmiş adı olduğu gibi döndürür) yeterli.

/** Emoji adını native surrogate'e normalize eder. TEBHOS'ta emojiName zaten native
 *  karakter olduğundan passthrough. kaynak-referansta bu bir shortcode→surrogate tablosuydu. */
function normalizeEmojiNameToSurrogate(name: string): string {
  return name;
}

export interface CustomStatus {
  text: string | null;
  expiresAt: string | null;
  emojiId: string | null;
  emojiName: string | null;
  emojiAnimated?: boolean | null;
}

export interface GatewayCustomStatusPayload {
  text?: string | null;
  expires_at?: string | null;
  emoji_id?: string | null;
  emoji_name?: string | null;
  emoji_animated?: boolean | null;
}

export interface ApiCustomStatusPayload {
  text?: string | null;
  expires_at?: string | null;
  emoji_id?: string;
  emoji_name?: string;
}

export const CUSTOM_STATUS_TEXT_LIMIT = 128;

function trimToNonEmpty(value: string | null | undefined): string | null {
  if (value == null) return null;
  const t = value.trim();
  return t.length === 0 ? null : t;
}

function clampText(value: string | null | undefined): string | null {
  const t = trimToNonEmpty(value);
  return t == null ? null : t.slice(0, CUSTOM_STATUS_TEXT_LIMIT);
}

function expiryMs(expiresAt: string | null | undefined): number | null {
  if (expiresAt == null) return null;
  const ms = Date.parse(expiresAt);
  return Number.isFinite(ms) ? ms : null;
}

/** Durumun süresi geçmiş mi? expiresAt yoksa asla geçmez. */
export function isCustomStatusExpired(status: CustomStatus | null, referenceTime: number = Date.now()): boolean {
  const ms = expiryMs(status?.expiresAt);
  if (ms == null) return false;
  return ms <= referenceTime;
}

/** Metni kırpar, emoji alanlarını normalize eder, tamamen boş/süresi geçmişse null döner. */
export function normalizeCustomStatus(status: CustomStatus | null | undefined): CustomStatus | null {
  if (status == null) return null;
  const text = clampText(status.text);
  const emojiId = trimToNonEmpty(status.emojiId);
  const rawEmojiName = trimToNonEmpty(status.emojiName);
  const emojiName =
    rawEmojiName == null
      ? null
      : emojiId != null
        ? rawEmojiName
        : normalizeEmojiNameToSurrogate(rawEmojiName);
  if (text == null && emojiId == null && emojiName == null) return null;
  const normalized: CustomStatus = {
    text,
    expiresAt: status.expiresAt ?? null,
    emojiId,
    emojiName,
    emojiAnimated: status.emojiAnimated ?? null,
  };
  return isCustomStatusExpired(normalized) ? null : normalized;
}

/** Uygulama modelini gateway (snake_case) payload'una çevirir. */
export function toGatewayCustomStatus(status: CustomStatus | null | undefined): GatewayCustomStatusPayload | null {
  if (status == null) return null;
  return {
    text: status.text,
    expires_at: status.expiresAt,
    emoji_id: status.emojiId,
    emoji_name: status.emojiName,
    emoji_animated: status.emojiAnimated ?? undefined,
  };
}

/** Normalize edip API PATCH payload'una çevirir (emoji_id öncelikli, yoksa emoji_name). */
export function toApiCustomStatusPayload(status: CustomStatus | null | undefined): ApiCustomStatusPayload | null {
  const normalized = normalizeCustomStatus(status);
  if (normalized == null) return null;
  const payload: ApiCustomStatusPayload = { text: normalized.text, expires_at: normalized.expiresAt };
  if (normalized.emojiId != null) {
    payload.emoji_id = normalized.emojiId;
  } else if (normalized.emojiName != null) {
    payload.emoji_name = normalizeEmojiNameToSurrogate(normalized.emojiName);
  }
  return payload;
}

/** Gateway payload'unu uygulama modeline çevirir (normalize edilmiş). */
export function fromGatewayCustomStatus(payload: GatewayCustomStatusPayload | null | undefined): CustomStatus | null {
  if (payload == null) return null;
  return normalizeCustomStatus({
    text: payload.text ?? null,
    expiresAt: payload.expires_at ?? null,
    emojiId: payload.emoji_id ?? null,
    emojiName: payload.emoji_name ?? null,
    emojiAnimated: payload.emoji_animated ?? null,
  });
}

/** İki durumu ucuzca karşılaştırmak için deterministik anahtar üretir (memo/eşitlik). */
export function customStatusToKey(status: CustomStatus | null | undefined): string {
  if (status == null) return "";
  const fields: ReadonlyArray<string> = [
    status.text ?? "",
    status.emojiId ?? "",
    status.emojiName ?? "",
    String(status.emojiAnimated ?? ""),
    status.expiresAt ?? "",
  ];
  return fields.join("|");
}

/** Görüntülenebilir metni döner (kırpılmış, boşsa null). */
export function getCustomStatusText(status: CustomStatus | null | undefined): string | null {
  return trimToNonEmpty(status?.text);
}
